import { Injectable, NgZone } from '@angular/core'
import { Subject, BehaviorSubject } from 'rxjs'

export interface AudioStreamingStatus {
  isStreaming: boolean
  isConnected: boolean
  error?: string
}

export interface AudioLevelEvent {
  level: number
  isSpeaking: boolean
}

@Injectable({
  providedIn: 'root',
})
export class AudioStreamingService {
  private audioContext: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  private mediaStream: MediaStream | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null

  private isInitialized = false
  private isStreaming = false
  private sequenceNumber = 0

  // Audio level monitoring
  private analyserNode: AnalyserNode | null = null
  private levelCheckInterval: ReturnType<typeof setInterval> | null = null
  private readonly SILENCE_THRESHOLD = 10 // RMS threshold for speech detection

  // Subjects for reactive updates
  private statusSubject = new BehaviorSubject<AudioStreamingStatus>({
    isStreaming: false,
    isConnected: false,
  })
  private audioChunkSubject = new Subject<{
    data: ArrayBuffer
    sequence: number
  }>()
  private audioLevelSubject = new Subject<AudioLevelEvent>()

  // Public observables
  public status$ = this.statusSubject.asObservable()
  public audioChunk$ = this.audioChunkSubject.asObservable()
  public audioLevel$ = this.audioLevelSubject.asObservable()

  constructor(private zone: NgZone) {}

  /**
   * Initialize the AudioWorklet processor
   */
  async initialize(deviceId?: string | null): Promise<void> {
    if (this.isInitialized) {
      console.log('[AudioStreaming] Already initialized')
      return
    }

    try {
      console.log('[AudioStreaming] Initializing...')

      // Create AudioContext
      this.audioContext = new AudioContext({
        sampleRate: 48000, // Use native sample rate, worklet will resample
      })

      // Load AudioWorklet module
      const workletUrl = 'assets/audio-worklet/pcm-processor.js'
      await this.audioContext.audioWorklet.addModule(workletUrl)
      console.log('[AudioStreaming] AudioWorklet module loaded')

      // Get microphone access
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
        },
      }

      this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      console.log('[AudioStreaming] Microphone access granted')

      // Create source node from microphone
      this.sourceNode = this.audioContext.createMediaStreamSource(
        this.mediaStream,
      )

      // Create analyser for audio level monitoring
      this.analyserNode = this.audioContext.createAnalyser()
      this.analyserNode.fftSize = 256
      this.sourceNode.connect(this.analyserNode)

      // Create AudioWorklet node
      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        'pcm-processor',
      )

      // Initialize worklet with sample rate
      this.workletNode.port.postMessage({
        type: 'init',
        data: { sampleRate: this.audioContext.sampleRate },
      })

      // Handle PCM data from worklet
      this.workletNode.port.onmessage = (event) => {
        if (event.data.type === 'pcm' && this.isStreaming) {
          this.zone.run(() => {
            this.audioChunkSubject.next({
              data: event.data.data,
              sequence: this.sequenceNumber++,
            })
          })
        }
      }

      // Connect nodes: source -> analyser -> worklet
      this.sourceNode.connect(this.workletNode)
      // Note: We don't connect worklet to destination (no audio output needed)

      this.isInitialized = true
      this.statusSubject.next({
        isStreaming: false,
        isConnected: true,
      })

      console.log('[AudioStreaming] Initialization complete')
    } catch (error) {
      console.error('[AudioStreaming] Initialization failed:', error)
      this.statusSubject.next({
        isStreaming: false,
        isConnected: false,
        error: error instanceof Error ? error.message : 'Initialization failed',
      })
      throw error
    }
  }

  /**
   * Start streaming audio
   */
  async startStreaming(): Promise<void> {
    if (!this.isInitialized) {
      console.error('[AudioStreaming] Not initialized')
      return
    }

    if (this.isStreaming) {
      console.log('[AudioStreaming] Already streaming')
      return
    }

    console.log('[AudioStreaming] Starting streaming')

    // Resume audio context if suspended
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume()
    }

    // Tell worklet to start processing
    this.workletNode?.port.postMessage({ type: 'start' })

    this.isStreaming = true
    this.sequenceNumber = 0

    // Start audio level monitoring
    this.startLevelMonitoring()

    this.statusSubject.next({
      isStreaming: true,
      isConnected: true,
    })
  }

  /**
   * Stop streaming audio
   */
  stopStreaming(): void {
    if (!this.isStreaming) {
      return
    }

    console.log('[AudioStreaming] Stopping streaming')

    // Tell worklet to stop and flush buffer
    this.workletNode?.port.postMessage({ type: 'stop' })

    this.isStreaming = false

    // Stop audio level monitoring
    this.stopLevelMonitoring()

    this.statusSubject.next({
      isStreaming: false,
      isConnected: true,
    })
  }

  /**
   * Start audio level monitoring for voice activity detection
   */
  private startLevelMonitoring(): void {
    if (this.levelCheckInterval) {
      return
    }

    const dataArray = new Uint8Array(
      this.analyserNode?.frequencyBinCount || 128,
    )

    this.levelCheckInterval = setInterval(() => {
      if (!this.analyserNode) return

      this.analyserNode.getByteFrequencyData(dataArray)

      // Calculate RMS (Root Mean Square) for audio level
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i] * dataArray[i]
      }
      const rms = Math.sqrt(sum / dataArray.length)

      const isSpeaking = rms > this.SILENCE_THRESHOLD

      this.zone.run(() => {
        this.audioLevelSubject.next({
          level: rms,
          isSpeaking,
        })
      })
    }, 50) // Check every 50ms
  }

  /**
   * Stop audio level monitoring
   */
  private stopLevelMonitoring(): void {
    if (this.levelCheckInterval) {
      clearInterval(this.levelCheckInterval)
      this.levelCheckInterval = null
    }
  }

  /**
   * Clean up all resources
   */
  async cleanup(): Promise<void> {
    console.log('[AudioStreaming] Cleaning up')

    this.stopStreaming()

    // Disconnect nodes
    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }

    if (this.workletNode) {
      this.workletNode.disconnect()
      this.workletNode = null
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect()
      this.analyserNode = null
    }

    // Stop media stream tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }

    // Close audio context
    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }

    this.isInitialized = false
    this.statusSubject.next({
      isStreaming: false,
      isConnected: false,
    })
  }
}
